/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { Select } from "@eds/react";
import type { Option } from "@eds/react";
import { useCallback } from "react";
import type { FilterOption } from "../core/Directory.types";

export type DirectorySelectProps = {
	options: FilterOption[];
	value: string;
	onChange: (value: string) => void;
	/**
	 * Accessible label for the select. Required so the rendered control
	 * always has an a11y name (WCAG 4.1.2). Use the FilterDef's `label`.
	 */
	label: string;
	placeholder?: string;
	/** Hide the visible label (`label` is always applied as the input's `aria-label`). */
	hideLabel?: boolean;
	/** aria-label for the clear button (the select is always clearable). */
	clearLabel?: string;
};

/**
 * Single-value dropdown filter. Wraps EDS `Select` in non-multi mode with a clearable
 * control so the user can remove the current selection.
 */
export function DirectorySelect({
	options,
	value,
	onChange,
	label,
	placeholder,
	hideLabel,
	clearLabel,
}: DirectorySelectProps): React.JSX.Element {
	if (process.env.NODE_ENV !== "production" && !label.trim()) {
		console.warn(
			"[DirectorySelect] An empty `label` was passed; the rendered select will have no accessible name. Supply a meaningful label or set a localized fallback at the FilterDef.",
		);
	}

	const edsOptions: Option<string>[] = options.map((o) => ({
		label: o.label,
		value: o.value,
		disabled: o.disabled,
	}));

	const selected: Option<string> | null = value ? (edsOptions.find((o) => o.value === value) ?? null) : null;

	const handleChange = useCallback(
		(next: Option<string> | null) => {
			onChange(next?.value ?? "");
		},
		[onChange],
	);

	return (
		<Select<string, false, true>
			label={hideLabel ? undefined : label}
			inputProps={{ "aria-label": label }}
			placeholder={placeholder}
			options={edsOptions}
			value={selected}
			onChange={handleChange}
			clearable
			clearLabel={clearLabel}
		/>
	);
}
